/**
 * PDF writer for the watchlist export matrix (PV18, SE-8360).
 *
 * `jspdf` and `jspdf-autotable` load behind `await import()` as the Shadow
 * Team field export does, so a user who never exports never downloads them.
 * Landscape past 6 columns, one title line, the header repeated on every
 * page, and page numbers.
 * @module features/scouting/utils/watchlistExport/renderWatchlistPdf
 */

import type { ExportMatrix } from "./exportMatrix";

/** Columns above which the page turns landscape. */
const PORTRAIT_MAX_COLUMNS = 6;
/** Page margin in points. */
const MARGIN = 40;
/** Baseline of the title line. */
const TITLE_Y = 40;
/** First row of the table. */
const TABLE_START_Y = 56;
/** Font sizes in points. */
const TITLE_FONT_SIZE = 12;
const TABLE_FONT_SIZE = 8;
const FOOTER_FONT_SIZE = 8;
/** Distance of the page-number baseline from the bottom edge. */
const FOOTER_OFFSET = 16;

/** The part of a jsPDF document this writer touches. */
export interface WatchlistPdfDocument {
  text: (
    text: string,
    x: number,
    y: number,
    options?: { align?: "left" | "right" | "center" }
  ) => unknown;
  setFontSize: (size: number) => unknown;
  setPage: (page: number) => unknown;
  getNumberOfPages: () => number;
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  save: (filename: string) => unknown;
}

/** A jsPDF constructor as the lazy module exposes it. */
type JsPdfConstructor = new (options: {
  orientation: "portrait" | "landscape";
  unit: "pt";
  format: "a4";
}) => WatchlistPdfDocument;

/** The autotable plugin function as the lazy module exposes it. */
type AutoTable = (doc: unknown, options: Record<string, unknown>) => unknown;

/** Module loaders, injectable so tests never load the real libraries. */
export interface PdfModuleLoaders {
  readonly loadJsPdf: () => Promise<{ jsPDF: JsPdfConstructor }>;
  readonly loadAutoTable: () => Promise<AutoTable>;
}

const DEFAULT_LOADERS: PdfModuleLoaders = {
  loadJsPdf: async () =>
    (await import("jspdf")) as unknown as { jsPDF: JsPdfConstructor },
  loadAutoTable: async () =>
    (await import("jspdf-autotable")).default as unknown as AutoTable,
};

/** Text above the table. */
export interface WatchlistPdfMeta {
  /** "{list name} · {N players} · {order: preference | sorted by X}" */
  readonly title: string;
}

/**
 * Writes "Page n of N" at the bottom right of every page.
 * @param pdf - The rendered document
 */
const numberPages = (pdf: WatchlistPdfDocument): void => {
  const pageCount = pdf.getNumberOfPages();
  const x = pdf.internal.pageSize.getWidth() - MARGIN;
  const y = pdf.internal.pageSize.getHeight() - FOOTER_OFFSET;
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
  pdf.setFontSize(FOOTER_FONT_SIZE);
  pages.forEach(page => {
    pdf.setPage(page);
    pdf.text(`Page ${page} of ${pageCount}`, x, y, { align: "right" });
  });
};

/**
 * Renders the matrix into a jsPDF document. The caller saves it.
 * @param matrix - Header and rows from `buildExportMatrix`
 * @param meta - The title line
 * @param loaders - Module loaders (tests inject fakes)
 * @returns The rendered document.
 */
export const renderWatchlistPdf = async (
  matrix: ExportMatrix,
  meta: WatchlistPdfMeta,
  loaders: PdfModuleLoaders = DEFAULT_LOADERS
): Promise<WatchlistPdfDocument> => {
  const [{ jsPDF }, autoTable] = await Promise.all([
    loaders.loadJsPdf(),
    loaders.loadAutoTable(),
  ]);
  const pdf = new jsPDF({
    orientation:
      matrix.columns.length > PORTRAIT_MAX_COLUMNS ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });
  pdf.setFontSize(TITLE_FONT_SIZE);
  pdf.text(meta.title, MARGIN, TITLE_Y);
  autoTable(pdf, {
    startY: TABLE_START_Y,
    head: [matrix.columns.map(column => column.header)],
    body: matrix.rows.map(row => [...row]),
    showHead: "everyPage",
    styles: { fontSize: TABLE_FONT_SIZE, overflow: "linebreak" },
    margin: { top: TABLE_START_Y, bottom: MARGIN, left: MARGIN, right: MARGIN },
  });
  numberPages(pdf);
  return pdf;
};
